<template>
  <header :class="b()">
    <div :class="b('content')">
      <a
        :class="b('logo-wrapper')"
        :href="getRedirectPath"
      >
        <img
          :class="b('logo')"
          :alt="$t('c-header.logoAlt')"
          :src="sessionStore.logoUrl"
          width="284"
          height="151"
        />
      </a>
      <ul :class="b('navigation')">
        <slot name="customMenu">
          <li>
            <c-ids-language-menu />
          </li>
          <li
            v-for="page in sessionStore.mainNavigation.pages.slice(0, 3)"
            :key="page.id"
          >
            <a
              :href="page.uri"
              :target="page.target"
              :title="page.title"
              :class="b('navigation-link', { active: page.active })"
              :rel="page.target === '_blank' ? 'noopener noreferrer' : null"
              role="button"
            >
              <e-icon-sprite :icon="`i-${page.idsIcon || 'star'}`" />
            </a>
          </li>
          <li
            v-if="sessionStore.isLoginEnabled || sessionStore.getIsLoggedIn"
            :class="b('user-item')"
          >
            <a
              href="#"
              :class="b('navigation-link')"
              :title="
                $t(
                  sessionStore.getIsLoggedIn
                    ? 'c-user-navigation.logoutButtonText'
                    : 'c-user-navigation.loginButtonText',
                )
              "
              role="button"
              @click.prevent="onAuthButtonClicked"
            >
              <e-icon-sprite :icon="sessionStore.getIsLoggedIn ? 'i-user-logout' : 'i-user-login'" />
            </a>
          </li>
        </slot>
      </ul>
    </div>

    <template v-if="!minimal">
      <c-notification-container />
      <c-confirmation-modal />

      <portal-target
        name="modal-container"
        multiple
      />
      <portal-target
        name="flyout-container"
        multiple
      />
      <portal-target
        name="tooltip"
        multiple
      />

      <c-global-loader />
    </template>
  </header>
</template>

<script>
  import { PortalTarget } from 'portal-vue';
  import { URL_PARAM_MAP } from '@/setup/globals';
  import useSessionStore from '@/stores/session';
  import cGlobalLoader from '@/components/c-global-loader.vue';
  import cIdsLanguageMenu from '@/components/c-ids-language-menu.vue';
  import cNotificationContainer from '@/components/c-notification-container.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import api from '@/helpers/api.js';
  import { isCdcAvailable } from '@/plugins/cdc/shared.js';
  import cConfirmationModal from '@/plugins/confirmation-modal/c-confirmation-modal.vue';

  /**
   * Renders the page header.
   */
  export default {
    name: 'c-ids-header',
    components: {
      portalTarget: PortalTarget,
      cConfirmationModal,
      eIconSprite,
      cNotificationContainer,
      cGlobalLoader,
      cIdsLanguageMenu,
    },
    // mixins: [],

    props: {
      /**
       * Define if the header should be rendered in minimal mode.
       */
      minimal: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      return {
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        screensaverTimer: null,
        idleInterruptEvents: ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'ontouchstart', 'keypress'],
      };
    },

    computed: {
      /**
       * Returns the redirect path.
       *
       * @returns {String}
       */
      getRedirectPath() {
        return this.sessionStore.startPath || this.sessionStore.basePath || '/';
      },

      /**
       * Checks if the screensaver should be shown.
       *
       * @returns {Boolean}
       */
      isScreensaverActive() {
        const search = new URLSearchParams(window.location.search);

        return this.sessionStore.isScreensaverActive && !!search.get(URL_PARAM_MAP.screensaver);
      },
    },
    watch: {
      'sessionStore.isScreensaverActive': function (isActive) {
        if (isActive) {
          this.removeEventsForScreensaverTimer();
        } else {
          this.setEventsForScreensaverTimer();
        }
      },
    },
    // beforeCreate() {},
    // created() { },
    // beforeMount() {},
    mounted() {
      this.setEventsForScreensaverTimer();
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      this.removeEventsForScreensaverTimer();
    },
    // unmounted() {},
    methods: {
      /**
       * Sets the events for the screensaver timer.
       */
      setEventsForScreensaverTimer() {
        if (this.minimal || this.isScreensaverActive) {
          return;
        }

        this.idleInterruptEvents.forEach((event) => {
          window.addEventListener(event, this.restartScreensaverTimer);
        });

        this.restartScreensaverTimer();
      },

      /**
       * Removes the events for the screensaver timer.
       */
      removeEventsForScreensaverTimer() {
        clearTimeout(this.screensaverTimer);

        this.idleInterruptEvents.forEach((event) => {
          window.removeEventListener(event, this.restartScreensaverTimer);
        });
      },

      /**
       * Resets the screensaver timer.
       */
      restartScreensaverTimer() {
        clearTimeout(this.screensaverTimer);
        this.screensaverTimer = setTimeout(this.redirectToScreensaver, this.sessionStore.screensaverDelay);
      },

      /**
       * Redirects to the screensaver.
       */
      redirectToScreensaver() {
        this.removeEventsForScreensaverTimer();

        const search = new URLSearchParams();

        search.set(URL_PARAM_MAP.screensaver, 'true');

        // TODO: The check for CDC is needed as CDC often changes things and we get errors on the S system.
        if (this.sessionStore.getIsLoggedIn && isCdcAvailable()) {
          this.$cdc.auth.logout(`${this.getRedirectPath}?${search.toString()}`);
        } else {
          window.location.href = `${this.getRedirectPath}?${search.toString()}`;
        }
      },

      /**
       * Either triggers the login or logs the user out.
       */
      onAuthButtonClicked() {
        if (this.sessionStore.getIsLoggedIn) {
          this.$cdc.auth.pimcoreLogout();
        } else {
          const search = new URLSearchParams();

          search.set(URL_PARAM_MAP.referrerURL, window.location.href);

          window.location.href = `${api.getUrl('azure-login')}?${search.toString()}`;
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-ids-header {
    $this: &;

    @include mixins.z-index(navigation);

    position: sticky;
    top: 0;
    flex: 0 0 auto;
    height: functions.rem(variables.$shared--ids-header-height);
    background-color: variables.$color-grayscale--1000;

    &__content {
      @extend %layout;
      @include mixins.font(variables.$font-size--14);

      display: flex;
      justify-content: space-between;
      align-items: center;
      max-width: functions.rem(map.get(variables.$breakpoints, lg) - (variables.$spacing--60 * 2));
      height: 100%;
      padding: 0 functions.rem(variables.$spacing--50);

      @include mixins.media(md) {
        max-width: none;
      }

      @include mixins.media(lg) {
        max-width: functions.rem(map.get(variables.$breakpoints, lg), variables.$font-size--lg-scale-basis);
      }
    }

    &__logo-wrapper {
      display: block;
      flex: 0 0 functions.rem(200px);
      margin-right: functions.rem(variables.$spacing--80);
    }

    &__logo {
      max-width: functions.rem(200px);
      height: auto;
    }

    &__navigation {
      @extend %list-reset;

      display: flex;
      align-items: center;
      height: 100%;

      > li {
        margin-left: functions.rem(variables.$spacing--40);
      }
    }

    &__navigation-link {
      color: variables.$color-primary--1;
      transition: color variables.$transition-duration--200 ease-in-out;

      &--active {
        color: variables.$color-primary--2;
      }

      .e-icon-sprite {
        width: functions.rem(34px);
        height: functions.rem(34px);
      }
    }

    &__user-item {
      padding-left: functions.rem(variables.$spacing--30);
    }
  }
</style>
